import React, {useState} from "react";
import {useTranslation} from "react-i18next";
import {Modal, ModalBody, ModalFooter, ModalHeader} from "reactstrap";
import ButtonIcon from "../../atoms/button/buttonIcon";
import {useStores} from "../../../stores/useStores";
import CircleIcon from "../../atoms/circleIcon/circleIcon";
import {IconSource} from "../../../shared/enums/iconSource";
import NavigationButton from "../../atoms/button/navigationButton";
import "./exportTransactionsModal.sass";
import {residentCsvExporterConfigs} from "../../../shared/residentCsvExporterConfigs";
import CsvDownloader from "react-csv-downloader";
import ProcessedBooking from "../../../stores/domainStore/pages/models/processedBooking";
import {addMonths, format} from "date-fns";
import {observer} from "mobx-react-lite";
import {TimeZones} from "../../../shared/enums/timeZones";
import {ToastMessageStatus} from "../../atoms/toastMessage/toastMessage";
import {utcToZonedTime} from "date-fns-tz";
import {convertToTwoDecimalPlaces} from "../../../utils/stringUtils";
import {TransactionStatus} from "../../../shared/enums/transactionStatus";
import {ProcessedBookingsCSVSortUtil} from "../../../utils/sortUtils/processedBookingsCsvSortUtil";
import DateEdit from "../dateEdit/dateEdit";
import {DateObservable} from "../../../stores/observables/dateObservable";

interface IExportTransactionsModal {
  isOpen: boolean;
  toggle: () => void;
  selectedDate: DateObservable;
}

const ExportTransactionsModal: React.FC<IExportTransactionsModal> = ({
  isOpen,
  toggle,
  selectedDate
}) => {
  const {
    uiStore: {selectableVillages, toastMessageStore},
    domainStore: {transactionsDomainStore},
    userService
  } = useStores();

  const timezone =
    userService?.getSelectedVillage()?.timeZone ?? TimeZones.Auckland;

  const {t} = useTranslation(["translation", "event"]);

  const [isExporting, setIsExporting] = useState(false);

  const fileName =
    selectableVillages.selectedItem && selectedDate.date
      ? `${
          selectableVillages.selectedItem.label
        } - ${selectedDate.date.toLocaleString("default", {
          month: "long",
          year: "numeric"
        })} - Transactions`
      : "Transactions";

  type key = keyof ProcessedBooking;
  const columns: {id: key; displayName: string}[] = [
    {
      id: "timestamp",
      displayName: t("event:labels.transactionsTable.eventDate")
    },
    {
      id: "createdDate",
      displayName: t("event:labels.transactionsTable.dateCharged")
    },
    {
      id: "eventTitle",
      displayName: t("event:labels.transactionsTable.event")
    },
    {
      id: "payeeName",
      displayName: t("event:labels.transactionsTable.chargedTo")
    },
    {
      id: "transactionDetails",
      displayName: t("event:labels.transactionsTable.description")
    },
    {
      id: "price",
      displayName: t("event:labels.transactionsTable.cost")
    },
    {
      id: "isSuccess",
      displayName: t("event:labels.transactionsTable.status")
    }
  ];

  const getExportTransactions = async () => {
    if (!selectedDate.date) return [];

    setIsExporting(true);
    const firstDayOfSelectedMonth = new Date(
      selectedDate.date.getFullYear(),
      selectedDate.date.getMonth(),
      1
    );
    const lastDaySelectedMonth = addMonths(firstDayOfSelectedMonth, 1);

    const transactions = await transactionsDomainStore.getTransactions(
      firstDayOfSelectedMonth,
      lastDaySelectedMonth
    );

    const sortedTransactions = new ProcessedBookingsCSVSortUtil(
      transactions.events
    ).sortForCSV();

    setIsExporting(false);
    return sortedTransactions.map((t: ProcessedBooking) => ({
      timestamp: format(
        utcToZonedTime(t.timestamp, timezone),
        "dd/MM/yyyy HH:mm"
      ),
      createdDate: format(
        utcToZonedTime(t.createdDate, timezone),
        "dd/MM/yyyy"
      ),
      eventTitle: t.eventTitle,
      transactionDetails: t.transactionDetails,
      payeeName: t.payeeName,
      price: `$${convertToTwoDecimalPlaces(t.price)}`,
      isSuccess: t.isSuccess
        ? TransactionStatus.Confirmed.toString()
        : TransactionStatus.Error.toString()
    }));
  };

  const onError = () => {
    setIsExporting(false);
    toastMessageStore.displayToastMessage(
      t("event:errors.downloadFailed"),
      ToastMessageStatus.Danger
    );
  };
  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      modalClassName="theme-modal export-transactions-modal"
      contentClassName="border"
      centered
    >
      <ModalHeader
        toggle={toggle}
        className="px-card bg-light border-0 flex-between-center"
        style={{wordBreak: "break-word"}}
        close={<NavigationButton icon={IconSource.exit} onClick={toggle} />}
      >
        {t("event:titles.exportAccount")}
      </ModalHeader>
      <ModalBody className="pt-3">
        <>
          <div className="message">{t("event:messages.exportAccount")}</div>
          <div className="d-inline-flex flex-center village">
            <div className="mr-3">
              <CircleIcon icon={IconSource.home} />
            </div>
            <div className="d-inline-flex">
              <div className="name pr-2">
                {t("event:labels.retirementVillage", {
                  name: `${selectableVillages?.selectedItem?.label}`
                })}
              </div>
              <div className="type">{t("translation:titles.residents")}</div>
            </div>
          </div>
          <br />
          <DateEdit
            label={t("event:labels.date")}
            overrideIsRequired={true}
            value={selectedDate}
            dateFormat={"MM/yyyy"}
            timeZone={timezone}
            placeholder={t("event:placeholders.selectMonth")}
            showTimeSelect={false}
            validationMessage={t("specialDays:errors:date")}
            disabled={isExporting}
            showMonthYearPicker={true}
            position="bottom"
            maxDate={new Date()}
          />
        </>
      </ModalBody>
      <ModalFooter className="bg-light border-top-0 flex-between-center">
        <div className="w-75">
          {isExporting && (
            <div className="font-italic export-event-message">
              {t("event:messages.exportingMessage")}
            </div>
          )}
        </div>
        <div>
          <CsvDownloader
            filename={fileName}
            extension={residentCsvExporterConfigs.fileExtension}
            wrapColumnChar={residentCsvExporterConfigs.wrapColumnChar}
            columns={columns}
            datas={getExportTransactions}
            handleError={onError}
          >
            <ButtonIcon
              type="secondary"
              icon={IconSource.export}
              isDisabled={isExporting || selectedDate.date == null}
            >
              {t("translation:actions.export")}
            </ButtonIcon>
          </CsvDownloader>
        </div>
      </ModalFooter>
    </Modal>
  );
};

export default observer(ExportTransactionsModal);
